import React, { useRef } from "react";
import { useEffect, useContext } from "react";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ERROR_TIMEOUT_API_ACTIVATION_DELAY,
  GETTING_STARTED_DOCUMENTATION_CLICK,
  GETTING_STARTED_PAGE_VIEW,
  GETTING_STARTED_VIEW_API_CREDENTIALS_CLICK,
  trackEvent,
} from "../../analytics";
import { sdkConnectApi } from "../../api";
import { Icons, IntermediateLoader, InvertedPrimaryButton, PrimaryButton, Typography } from "../../components";
import Steps from "../../components/GettingStarted/GettingStarted";
import { getPageTitle, PHYLLO_USERS_API_TIMEOUT, PHYLLO_USERS_POLLING_INTERVAL } from "../../constants/constants";
import { docs_home } from "../../constants/urls";
import { UserContext, WarehouseContext } from "../../storage/context";
import { ADD_USER_STEPS, UPDATE_TENANT_STATUS } from "../../storage/reducers/types";
import { getAuthKey, getClientIDAndSecret, getProductsList } from "../../utils/util";
import { Modal } from "antd";
import "./GettingStartedPage.scss";
import { useFetchTenantInformation } from "../../hooks/useFetchTenantInformation";

let expiryTimer = new Date().getTime() + PHYLLO_USERS_API_TIMEOUT;
let timer;

const GettingStartedPage = () => {
  const [accountId, setAccountId] = useState();
  // const { warehouse, dispatchWarehouse } = useContext(WarehouseContext);
  const {
    warehouse,
    warehouse: { tenantInfo, tenantStatus, environmentBaseURL, environmentChanged, environment },
    dispatchWarehouse,
  } = useContext(WarehouseContext);
  let navigate = useNavigate();
  const [currStep, setCurrStep] = useState(warehouse.userCurrentStep);
  const [loader, setLoader] = useState(false);
  const [userResponse, setUserResponse] = useState({});
  const { userProfile } = useContext(UserContext);
  const [changeEnvLoader, setChangeEnvLoader] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fetchTenantInformation = useFetchTenantInformation();
  const isMounted = useRef(false);

  const ErrorModalConfig = {
    title: (
      <Typography h3 style={{ fontWeight: "600" }}>
        Uh-oh! We are unable to reach our servers at this moment
      </Typography>
    ),
    content: (
      <div>
        <p className="contact-sales-card-description">
          It's not you, it's us. We are working on fixing it. Please refresh the page in some time or send a report to{" "}
          <a href="mailto:support@getphyllo.com" style={{ color: "#2262ED" }}>
            <u>support@getphyllo.com</u>
          </a>{" "}
          if the issue persists.
        </p>
      </div>
    ),
    closeIcon: <Icons.close_circle_fill onClick={() => navigate("/developers/api-credentials")} />,
    closable: true,
    icon: false,
    cancelText: null,
    okType: "text",
    okButtonProps: {
      style: { backgroundColor: "transparent", height: "65px" },
    },
    okText: (
      <InvertedPrimaryButton
        label="Got it!"
        onClick={() => {
          // TODO: Add active tab to Getting started ONLY

          navigate("/developers/api-credentials");
        }}
      />
    ),
  };

  const updateTenantStatus = async (tenantInfo) => {
    try {
      if (!userProfile.environment) return;
      const response = await sdkConnectApi.getAllUsers(tenantInfo, environmentBaseURL, environment);
      if (response.status === 200) {
        clearTimeout(timer);
        dispatchWarehouse({ type: UPDATE_TENANT_STATUS });
      }
    } catch (e) {
      if (new Date().getTime() > expiryTimer) {
        if (isModalOpen) {
          Modal.info(ErrorModalConfig);
          trackEvent(ERROR_TIMEOUT_API_ACTIVATION_DELAY);
          expiryTimer = new Date().getTime() + PHYLLO_USERS_API_TIMEOUT;
        }
        setIsModalOpen(true);
      } else {
        if (isMounted.current) {
          timer = setTimeout(() => {
            clearTimeout(timer);
            updateTenantStatus(tenantInfo);
          }, PHYLLO_USERS_POLLING_INTERVAL);
        }
      }
    }
  };

  useEffect(() => {
    try {
      if (!tenantStatus && tenantInfo.length !== 0) updateTenantStatus(tenantInfo);
    } catch (e) {
      throw e;
    }
  }, [isModalOpen, tenantInfo]);

  useEffect(() => {
    if (environmentChanged) {
      clearTimeout(timer);
      expiryTimer = new Date().getTime() + PHYLLO_USERS_API_TIMEOUT;
      fetchTenantInformation();
      // Creating Product list based on environment
      getProductsList(warehouse.environment.current);
      if (localStorage.getItem("SDK_USER_CREDS")) {
        localStorage.removeItem("SDK_USER_CREDS");
        localStorage.removeItem("User_Response");
        dispatchWarehouse({ type: ADD_USER_STEPS, payload: 1 });
        setChangeEnvLoader(false);
        setCurrStep(1);
        setAccountId();
        setChangeEnvLoader(true);
      }
    }
  }, [environmentChanged]);

  useEffect(() => {
    expiryTimer = new Date().getTime() + PHYLLO_USERS_API_TIMEOUT;
    trackEvent(GETTING_STARTED_PAGE_VIEW);
  }, []);

  // useEffect(() => {
  //   localStorage.removeItem("SDK_USER_CREDS");
  //   localStorage.removeItem("User_Response");
  // }, []);
  useEffect(() => {
    isMounted.current = true;

    // Creating Product list based on environment
    getProductsList(warehouse.environment.current);

    // To ensure clearing user creds with page refresh.
    window.onbeforeunload = function () {
      localStorage.removeItem("SDK_USER_CREDS");
      localStorage.removeItem("User_Response");
    };
    return () => {
      window.onbeforeunload = null;
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, []);

  return (
    <div>
      <h2 className="main_heading"> Integrate Phyllo in 3 steps</h2>
      {warehouse.tenantStatus && changeEnvLoader ? (
        <>
          <div>
            <Steps step={1} setCurrStep={setCurrStep} setLoader={setLoader} dispatchWarehouse={dispatchWarehouse} />
            {currStep >= 2 && (
              <Steps
                step={2}
                setLoader={setLoader}
                setCurrStep={setCurrStep}
                accountId={accountId}
                setAccountId={setAccountId}
                userResponse={userResponse}
                setUserResponse={setUserResponse}
                dispatchWarehouse={dispatchWarehouse}
              />
            )}
            {currStep === 3 && (
              <div>
                <Steps
                  step={3}
                  loader={loader}
                  setLoader={setLoader}
                  accountId={accountId}
                  setAccountId={setAccountId}
                  userResponse={userResponse}
                  setUserResponse={setUserResponse}
                  dispatchWarehouse={dispatchWarehouse}
                />
                {!loader && (
                  <>
                    <h1 className="done_heading">Done!</h1>
                    <p className="done_text">
                      That's how you use Phyllo APIs to fetch data of your users. Can't wait to see what you build with Phyllo! 🎉
                    </p>
                    <div className="done_button">
                      <PrimaryButton
                        label={"View API credentials"}
                        htmlType="submit"
                        onClick={() => {
                          navigate("/developers/api-credentials");
                          trackEvent(GETTING_STARTED_VIEW_API_CREDENTIALS_CLICK, {
                            url: window.location.href,
                          });
                        }}
                      />
                      <a href={docs_home} target="_blank">
                        <InvertedPrimaryButton
                          className="open_doc_but"
                          label={"Go to the documentation"}
                          style={{ backgroundColor: "white", width: "215px" }}
                          htmlType="submit"
                          onClick={() => {
                            trackEvent(GETTING_STARTED_DOCUMENTATION_CLICK, {
                              url: docs_home,
                            });
                          }}
                        />
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <Helmet>
            <title>{getPageTitle("GETTING_STARTED_PAGE")}</title>
          </Helmet>
        </>
      ) : (
        <IntermediateLoader content={<p style={{ fontSize: "14px" }}>Just a moment, we are setting up your Phyllo account.</p>} />
      )}
    </div>
  );
};

export default GettingStartedPage;
